import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  LayoutDashboard,
  Calendar,
  MessageSquare,
  LogOut,
  TrendingUp,
  Users,
  DollarSign,
  CheckCircle,
  Clock,
  XCircle
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminDashboard = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  
  const [stats, setStats] = useState(null);
  const [recentBookings, setRecentBookings] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      const [statsRes, bookingsRes] = await Promise.all([
        fetch(`${API}/admin/stats`, { credentials: 'include' }),
        fetch(`${API}/admin/bookings`, { credentials: 'include' })
      ]);

      const statsData = await statsRes.json();
      const bookingsData = await bookingsRes.json();

      setStats(statsData);
      setRecentBookings(bookingsData.slice(0, 5));
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Error fetching data:', error);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleLogout = async () => {
    await logout();
    navigate('/admin/login');
  };

  if (loading) {
    return (
      <div data-testid="dashboard-loading" className="min-h-screen flex items-center justify-center bg-[#FAF8F5]">
        <p className="text-[#3A3A3A]">Loading dashboard...</p>
      </div>
    );
  }

  const statCards = [
    {
      icon: Calendar,
      label: 'Total Bookings',
      value: stats?.total_bookings || 0,
      color: '#C9A84C'
    },
    {
      icon: CheckCircle,
      label: 'Confirmed',
      value: stats?.confirmed_bookings || 0,
      color: '#2E7D32'
    },
    {
      icon: Clock,
      label: 'Pending',
      value: stats?.pending_bookings || 0,
      color: '#FF9800'
    },
    {
      icon: DollarSign,
      label: 'Revenue',
      value: `₦${(stats?.total_revenue || 0).toLocaleString()}`,
      color: '#1A1A1A'
    }
  ];

  return (
    <div data-testid="admin-dashboard-page" className="min-h-screen bg-[#FAF8F5]">
      {/* Header */}
      <div className="bg-[#1A1A1A] text-[#FAF8F5] py-4 px-6 md:px-12">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <h1 className="font-serif text-2xl font-bold tracking-tight text-[#C9A84C]" data-testid="dashboard-logo">
              De Azraa
            </h1>
            <span className="text-xl font-medium">Admin Dashboard</span>
          </div>
          <div className="flex items-center gap-6">
            <span className="text-sm" data-testid="admin-email">
              {user?.email}
            </span>
            <button
              onClick={handleLogout}
              data-testid="logout-btn"
              className="flex items-center gap-2 text-sm hover:text-[#C9A84C] transition-colors"
            >
              <LogOut size={16} />
              Logout
            </button>
          </div>
        </div>
      </div>

      {/* Navigation */}
      <div className="bg-white border-b border-[#E5E5E5] px-6 md:px-12 py-4">
        <nav className="flex gap-8">
          <Link
            to="/admin/dashboard"
            data-testid="nav-dashboard"
            className="flex items-center gap-2 text-sm font-medium text-[#C9A84C] border-b-2 border-[#C9A84C] pb-2"
          >
            <LayoutDashboard size={16} />
            Dashboard
          </Link>
          <Link
            to="/admin/bookings"
            data-testid="nav-bookings"
            className="flex items-center gap-2 text-sm font-medium text-[#3A3A3A] hover:text-[#C9A84C] transition-colors pb-2"
          >
            <Calendar size={16} />
            Bookings
          </Link>
          <Link
            to="/admin/suites"
            data-testid="nav-suites"
            className="flex items-center gap-2 text-sm font-medium text-[#3A3A3A] hover:text-[#C9A84C] transition-colors pb-2"
          >
            <Users size={16} />
            Suites
          </Link>
          <Link
            to="/admin/contacts"
            data-testid="nav-contacts"
            className="flex items-center gap-2 text-sm font-medium text-[#3A3A3A] hover:text-[#C9A84C] transition-colors pb-2"
          >
            <MessageSquare size={16} />
            Contact Messages
          </Link>
        </nav>
      </div>

      {/* Main Content */}
      <div className="px-6 md:px-12 py-12">
        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
          {statCards.map((stat) => (
            <motion.div
              key={stat.label}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: statCards.indexOf(stat) * 0.1 }}
              data-testid={`stat-card-${statCards.indexOf(stat)}`}
              className="bg-white border border-[#E5E5E5] p-6"
            >
              <div className="flex items-center justify-between mb-4">
                <stat.icon size={24} style={{ color: stat.color }} strokeWidth={1.5} />
                <TrendingUp size={16} className="text-green-600" />
              </div>
              <p className="text-2xl font-serif font-light text-[#1A1A1A] mb-1" data-testid={`stat-value-${statCards.indexOf(stat)}`}>
                {stat.value}
              </p>
              <p className="text-xs text-[#3A3A3A] uppercase tracking-wider">{stat.label}</p>
            </motion.div>
          ))}
        </div>

        {/* Recent Bookings */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="bg-white border border-[#E5E5E5] p-6"
        >
          <div className="flex items-center justify-between mb-6">
            <h2 className="font-serif text-2xl tracking-tight font-light text-[#1A1A1A]">
              Recent Bookings
            </h2>
            <Link
              to="/admin/bookings"
              data-testid="view-all-bookings-link"
              className="text-sm text-[#C9A84C] hover:text-[#1A1A1A] transition-colors"
            >
              View All →
            </Link>
          </div>

          {recentBookings.length === 0 ? (
            <p className="text-[#3A3A3A] text-center py-8" data-testid="no-bookings">
              No bookings yet
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full" data-testid="bookings-table">
                <thead>
                  <tr className="border-b border-[#E5E5E5]">
                    <th className="text-left py-3 text-xs uppercase tracking-wider text-[#3A3A3A]">Guest</th>
                    <th className="text-left py-3 text-xs uppercase tracking-wider text-[#3A3A3A]">Suite</th>
                    <th className="text-left py-3 text-xs uppercase tracking-wider text-[#3A3A3A]">Check-in</th>
                    <th className="text-left py-3 text-xs uppercase tracking-wider text-[#3A3A3A]">Amount</th>
                    <th className="text-left py-3 text-xs uppercase tracking-wider text-[#3A3A3A]">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {recentBookings.map((booking) => (
                    <tr
                      key={booking.id}
                      data-testid={`booking-row-${booking.id}`}
                      className="border-b border-[#E5E5E5] hover:bg-[#FAF8F5] transition-colors"
                    >
                      <td className="py-4 text-sm text-[#1A1A1A]">{booking.guest_name}</td>
                      <td className="py-4 text-sm text-[#3A3A3A]">{booking.suite_name}</td>
                      <td className="py-4 text-sm text-[#3A3A3A]">
                        {new Date(booking.check_in).toLocaleDateString()}
                      </td>
                      <td className="py-4 text-sm text-[#1A1A1A] font-medium">
                        ₦{booking.total_amount.toLocaleString()}
                      </td>
                      <td className="py-4">
                        <span
                          className={`inline-flex items-center gap-1 px-3 py-1 text-xs font-medium ${
                            booking.booking_status === 'confirmed'
                              ? 'bg-green-50 text-green-800'
                              : booking.booking_status === 'pending'
                              ? 'bg-yellow-50 text-yellow-800'
                              : 'bg-red-50 text-red-800'
                          }`}
                        >
                          {booking.booking_status === 'confirmed' && <CheckCircle size={12} />}
                          {booking.booking_status === 'pending' && <Clock size={12} />}
                          {booking.booking_status === 'cancelled' && <XCircle size={12} />}
                          {booking.booking_status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </motion.div>
      </div>
    </div>
  );
};

export default AdminDashboard;
